import React, { useEffect, useState, useMemo } from 'react';
import { Table } from 'antd';
import { FormField } from 'components';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { supplierUpdateVoucher } from 'store/forms/actions';

const SupplierServiceCosts = ({ disableVoucherTabs, setDisableAction }) => {
  const dispatch = useDispatch();
  const voucherInfo = useSelector(state => state.forms?.supplier?.voucherInfo);
  const legalVat = useSelector(state => state.forms.carrier.legalVat);

  const debouncedUpdateVoucher = useMemo(
    () =>
      debounce((payload, dispatch) => {
        dispatch(supplierUpdateVoucher({ simulation: true, params: payload }));
      }, 1000),
    []
  );

  const initialData = voucherInfo?.voucher || {
    base: { amount: '-', vat: '-', final: '-' },
    additional: { amount: '-', vat: '-', final: '-' },
    voucher: { amount: '-', vat: '-', final: '-' },
    beneficiary: { amount: '-', vat: '-', final: '-' },
  };

  const [data, setData] = useState(initialData);

  const parseFormattedNumber = val => {
    if (typeof val === 'number') return val;
    if (!val) return 0;

    const normalized = val
      .toString()
      .replace(/\./g, '') // remove thousands separators
      .replace(',', '.'); // convert comma decimal to dot

    return Number(normalized);
  };

  const formatToCurrencyString = value => {
    if (value === null || value === undefined || value === '-' || value === '')
      return '-';

    const numberValue =
      typeof value === 'string' ? parseFormattedNumber(value) : Number(value);

    if (isNaN(numberValue)) return '-';

    return numberValue.toFixed(2).replace('.', ',');
  };

  const handleChange = (type, key, value) => {
    setData(prevData => {
      const updated = {
        ...prevData,
        [type]: {
          ...prevData[type],
          [key]: value,
        },
      };

      const safeNumber = val => {
        if (val === null || val === undefined || val === '-' || val === '')
          return 0;
        return parseFormattedNumber(val);
      };

      const payload = {
        voucherCode: voucherInfo?.voucher?.code || '',
        vatId: legalVat,
        basePrice: safeNumber(
          type === 'base' && key === 'amount' ? value : updated.base.amount
        ),
        baseVat: safeNumber(
          type === 'base' && key === 'vat' ? value : updated.base.vat
        ),
        action: 'RESERVE',
      };

      debouncedUpdateVoucher(payload, dispatch);
      return updated;
    });
  };

  useEffect(() => {
    const safeFormat = val => {
      if (val === null || val === undefined) return '-';
      return formatToCurrencyString(val);
    };

    setData({
      base: {
        amount: safeFormat(voucherInfo?.voucher?.basePrice),
        vat: safeFormat(voucherInfo?.voucher?.baseVat),
        final: safeFormat(voucherInfo?.voucher?.baseTotal),
      },
      voucher: {
        amount: safeFormat(voucherInfo?.voucher?.voucherValue),
        vat: safeFormat(voucherInfo?.voucher?.voucherValueVat),
        final: safeFormat(voucherInfo?.voucher?.voucherValueTotal),
      },
      beneficiary: {
        amount: safeFormat(voucherInfo?.voucher?.beneficiaryAmount),
        vat: safeFormat(voucherInfo?.voucher?.beneficiaryAmountVat),
        final: safeFormat(voucherInfo?.voucher?.beneficiaryAmountTotal),
      },
    });
  }, [voucherInfo?.voucher]);

  //Validation for inputs in order to disable actions as Reserve
  useEffect(() => {
    const isMissing = val =>
      val === undefined ||
      val === null ||
      val === '' ||
      val === '-' ||
      Number(val.replace(',', '.')) === 0;

    // Destructure values from the temporaryData instead of voucherInfo
    const { base } = data || {};

    let hasError = false;

    // Rule 1: basePrice & baseVat must always be present
    if (isMissing(base?.amount) || isMissing(base?.vat)) {
      hasError = true;
    }

    setDisableAction(hasError);
  }, [data]);

  const rows = [
    {
      key: 'base',
      label: 'Αγορά',
      editable: !disableVoucherTabs,
      ...data.base,
    },
    {
      key: 'voucher',
      label: 'Επιδότηση',
      editable: false,
      ...data.voucher,
    },
    {
      key: 'beneficiary',
      label: 'Πληρωμή Ωφελούμενου',
      editable: false,
      ...data.beneficiary,
    },
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      width: '40%',
    },
    {
      title: 'Ποσό',
      dataIndex: 'amount',
      key: 'amount',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          return (
            <FormField
              type="formattedNumber"
              disabled={!record.editable}
              value={value}
              onChange={val => handleChange(record.key, 'amount', val)}
            />
          );
        }
      },
    },
    {
      title: 'Φ.Π.Α.',
      dataIndex: 'vat',
      key: 'vat',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          return (
            <FormField
              type="formattedNumber"
              suffixSymbol="percentage" // options "percentage = %" or "currency = €"
              disabled={!record.editable}
              value={value}
              onChange={val => handleChange(record.key, 'vat', val)}
            />
          );
        }
      },
    },
    {
      title: 'Τελικό Ποσό',
      dataIndex: 'final',
      key: 'final',
      width: '20%',
      render: value => (
        <span className="bold font-size-16">
          {value} {value !== '-' ? '€' : ''}
        </span>
      ),
      width: '50%',
    },
  ];

  return (
    <div className="voucher-costs-table">
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        bordered={false}
        rowClassName={() => 'custom-row'}
      />
    </div>
  );
};

export default SupplierServiceCosts;
